import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const WorkshopAnalytics = ({ requests, invoices, vehicles }) => {
    // 1. Calculate Request Status Distribution
    const statusData = useMemo(() => {
        const counts = { Pending: 0, Approved: 0, Completed: 0, Rejected: 0 };
        requests.forEach(r => {
            const s = r.status || 'Pending';
            if (counts[s] !== undefined) counts[s]++;
            else counts.Pending++; // default fallback
        });
        return [
            { name: 'Pending', value: counts.Pending, color: '#facc15' }, // yellow
            { name: 'Approved', value: counts.Approved, color: '#3b82f6' }, // blue
            { name: 'Completed', value: counts.Completed, color: '#22c55e' }, // green
            { name: 'Rejected', value: counts.Rejected, color: '#ef4444' }  // red
        ].filter(d => d.value > 0);
    }, [requests]);

    // 2. Synthesize "Monthly Revenue" from Invoices (mock logic if dates missing, usually invoices have createdAt)
    const revenueData = useMemo(() => {
        // Group invoices by Month
        const months = {};
        invoices.forEach(inv => {
            const date = new Date(inv.createdAt || inv.date || Date.now());
            const key = date.toLocaleString('default', { month: 'short' });
            months[key] = (months[key] || 0) + (Number(inv.total) || 0);
        });

        // If no data, show placeholder last 6 months
        if (Object.keys(months).length === 0) {
            return [
                { name: 'Jan', revenue: 0 }, { name: 'Feb', revenue: 0 },
                { name: 'Mar', revenue: 0 }, { name: 'Apr', revenue: 0 },
                { name: 'May', revenue: 0 }, { name: 'Jun', revenue: 0 }
            ];
        }

        return Object.entries(months).map(([name, revenue]) => ({ name, revenue }));
    }, [invoices]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Revenue Chart */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl">
                <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <span className="text-emerald-400">💰</span> Revenue Overview
                </h3>
                <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={revenueData}>
                            <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="name" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip
                                contentStyle={{ backgroundColor: '#1f2937', borderColor: '#374151', color: '#fff' }}
                                itemStyle={{ color: '#10b981' }}
                            />
                            <Area type="monotone" dataKey="revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Request Status Chart */}
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 shadow-xl">
                <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                    <span className="text-blue-400">📊</span> Request Status
                </h3>
                <div className="h-[300px] w-full flex items-center justify-center">
                    {statusData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={statusData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    {statusData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: 'none' }} />
                                <Legend verticalAlign="bottom" height={36} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="text-center text-slate-500">
                            <p>No request data yet</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkshopAnalytics;
